import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { useColumnConfig } from '../hooks/useColumnConfig';
import { ProjectData } from '../services/api';
import {
  trackExportEvent,
  trackProjectViewEvent,
} from '../services/rumService';
import {
  formatCO2eSaved,
  formatImpactPerM2,
  formatNumber,
  formatTotalImpact,
  formatWeight,
} from '../utils/formatUtils';
import { ColumnConfigModal } from './ColumnConfigModal';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { TableCellTooltip } from './ui/table-cell-tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

interface ProjectTableProps {
  projects: ProjectData[];
  selectedYear: number;
}

// Building types are now dynamically determined from BTA room analysis
// See BuildingTypeService for the mapping from BuildingType IDs to labels

const getDataAccuracyLevel = (
  percentage: number
): { level: string; color: string } => {
  if (percentage >= 70) return { level: 'High', color: 'text-green-600' };
  if (percentage >= 40) return { level: 'Medium', color: 'text-yellow-600' };
  return { level: 'Low', color: 'text-red-600' };
};

const formatDateAsMonthYear = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const getCO2BudgetStatus = (
  project: ProjectData
): {
  status: string;
  badgeClass: string;
  percentage: number;
  type: 'over' | 'under' | 'no-data';
  tooltipText: string;
} => {
  if (!project.latestSnapshot?.snapshotData) {
    return {
      status: 'No budget',
      badgeClass: 'bg-gray-100 text-gray-600 border-gray-200',
      percentage: 0,
      type: 'no-data',
      tooltipText: 'No climate data available',
    };
  }

  const { climateImpact } = project.latestSnapshot.snapshotData;
  const totalImpact = climateImpact?.a1_a3;
  const budget = project.budget;

  if (!budget || isNaN(budget)) {
    return {
      status: 'No budget',
      badgeClass: 'bg-gray-100 text-gray-600 border-gray-200',
      percentage: 0,
      type: 'no-data',
      tooltipText: 'No climate budget set for this project',
    };
  }

  if (!totalImpact || isNaN(totalImpact)) {
    return {
      status: 'No data',
      badgeClass: 'bg-gray-100 text-gray-600 border-gray-200',
      percentage: 0,
      type: 'no-data',
      tooltipText: 'No climate impact data available',
    };
  }

  const difference = ((totalImpact - budget) / budget) * 100;

  if (difference > 0) {
    return {
      status: `${formatNumber(difference)}%`,
      badgeClass: 'bg-red-50 text-red-700 border-red-200',
      percentage: difference,
      type: 'over',
      tooltipText: `${formatNumber(difference)}% over project budget`,
    };
  } else {
    return {
      status: `${formatNumber(Math.abs(difference))}%`,
      badgeClass: 'bg-green-50 text-green-700 border-green-200',
      percentage: Math.abs(difference),
      type: 'under',
      tooltipText: `${formatNumber(
        Math.abs(difference)
      )}% under project budget`,
    };
  }
};

const exportToExcel = (
  projects: ProjectData[],
  year: number,
  visibleColumns: any[]
) => {
  const excelData = projects.map(project => {
    const snapshot = project.latestSnapshot?.snapshotData;
    const totalImpact = snapshot?.climateImpact?.a1_a3 || 0;
    const gfa = snapshot?.projectSettings?.gfa || project.size;
    const impactPerM2 = gfa > 0 ? totalImpact / gfa : 0;
    const a4Impact = snapshot?.climateImpact?.a4 || 0;
    const a4PerM2 = gfa > 0 ? a4Impact / gfa : 0;
    const a5Impact = snapshot?.climateImpact?.a5 || 0;
    const a5PerM2 = gfa > 0 ? a5Impact / gfa : 0;
    const budgetStatus = getCO2BudgetStatus(project);

    const dataAccuracy = snapshot?.dataAccuracy;
    const accuracyPercentage =
      dataAccuracy && dataAccuracy.numberOfProducts > 0
        ? Math.round(
            (dataAccuracy.numberOfSpecificProducts /
              dataAccuracy.numberOfProducts) *
              100
          )
        : 0;

    // Circularity data for export
    const circularity = snapshot?.circularity;
    const inflow = circularity?.inflow;
    const outflow = circularity?.outflow;

    const allData: Record<string, any> = {
      'Start Date': formatDateAsMonthYear(project.createdAt),
      'Project Name': project.name,
      'Building Type': project.buildingType?.label || 'Unknown',
      'Area (m²)': gfa,
      'A1-A3 Impact per GFA (kg CO₂e/m²)': Math.round(impactPerM2 * 100) / 100,
      'A1-A3 Impact (tons CO₂e)': Math.round((totalImpact / 1000) * 100) / 100,
      'A4 Impact per GFA (kg CO₂e/m²)': Math.round(a4PerM2 * 100) / 100,
      'A4 Impact (tons CO₂e)': Math.round((a4Impact / 1000) * 100) / 100,
      'A5 Impact per GFA (kg CO₂e/m²)': Math.round(a5PerM2 * 100) / 100,
      'A5 Impact (tons CO₂e)': Math.round((a5Impact / 1000) * 100) / 100,
      'CO₂ Budget Status': budgetStatus.status,
      'Data Accuracy (%)': accuracyPercentage,
      // Circularity metrics
      'Total Material (kg)': circularity?.totalMaterialKg || 0,
      'Inflow: Reused Materials (kg)': inflow?.reusedMaterialsKg || 0,
      'Inflow: Preserved Materials (kg)': inflow?.preservedMaterialsKg || 0,
      'Inflow: Recycled Pre-Consumer (kg)':
        inflow?.recycledContentPreConsumerKg || 0,
      'Inflow: Recycled Post-Consumer (kg)':
        inflow?.recycledContentPostConsumerKg || 0,
      'Inflow: Biomass Materials (kg)': inflow?.biomassMaterialsKg || 0,
      'Inflow: CO₂e Saved (kg)': inflow?.totalKgCO2eSaved || 0,
      'Outflow: Reusable Materials (kg)': outflow?.reusableMaterialsKg || 0,
      'Outflow: Recyclable Materials (kg)': outflow?.recyclableMaterialsKg || 0,
      'Outflow: Demolition Systems (kg)': outflow?.demolitionSystemsKg || 0,
      'Outflow: Installation Systems (kg)': outflow?.installationSystemsKg || 0,
    };

    const filteredData: Record<string, any> = {};
    visibleColumns.forEach(column => {
      if (column.id !== 'actions') {
        const key = column.label || column.id;
        switch (column.id) {
          case 'startDate':
            filteredData[key] = allData['Start Date'];
            break;
          case 'name':
            filteredData[key] = allData['Project Name'];
            break;
          case 'buildingType':
            filteredData[key] = allData['Building Type'];
            break;
          case 'gfa':
            filteredData[key] = allData['Area (m²)'];
            break;
          case 'impactPerGfa':
            filteredData[key] = allData['A1-A3 Impact per GFA (kg CO₂e/m²)'];
            break;
          case 'totalImpact':
            filteredData[key] = allData['A1-A3 Impact (tons CO₂e)'];
            break;
          case 'a4PerGfa':
            filteredData[key] = allData['A4 Impact per GFA (kg CO₂e/m²)'];
            break;
          case 'a4TotalImpact':
            filteredData[key] = allData['A4 Impact (tons CO₂e)'];
            break;
          case 'a5PerGfa':
            filteredData[key] = allData['A5 Impact per GFA (kg CO₂e/m²)'];
            break;
          case 'a5TotalImpact':
            filteredData[key] = allData['A5 Impact (tons CO₂e)'];
            break;
          case 'budget':
            filteredData[key] = allData['CO₂ Budget Status'];
            break;
          case 'dataAccuracy':
            filteredData[key] = allData['Data Accuracy (%)'];
            break;
          // Circularity cases
          case 'totalMaterialKg':
            filteredData[key] = allData['Total Material (kg)'];
            break;
          case 'inflowReusedMaterialsKg':
            filteredData[key] = allData['Inflow: Reused Materials (kg)'];
            break;
          case 'inflowPreservedMaterialsKg':
            filteredData[key] = allData['Inflow: Preserved Materials (kg)'];
            break;
          case 'inflowRecycledPreConsumerKg':
            filteredData[key] = allData['Inflow: Recycled Pre-Consumer (kg)'];
            break;
          case 'inflowRecycledPostConsumerKg':
            filteredData[key] = allData['Inflow: Recycled Post-Consumer (kg)'];
            break;
          case 'inflowBiomassMaterialsKg':
            filteredData[key] = allData['Inflow: Biomass Materials (kg)'];
            break;
          case 'inflowTotalKgCO2eSaved':
            filteredData[key] = allData['Inflow: CO₂e Saved (kg)'];
            break;
          case 'outflowReusableMaterialsKg':
            filteredData[key] = allData['Outflow: Reusable Materials (kg)'];
            break;
          case 'outflowRecyclableMaterialsKg':
            filteredData[key] = allData['Outflow: Recyclable Materials (kg)'];
            break;
          case 'outflowDemolitionSystemsKg':
            filteredData[key] = allData['Outflow: Demolition Systems (kg)'];
            break;
          case 'outflowInstallationSystemsKg':
            filteredData[key] = allData['Outflow: Installation Systems (kg)'];
            break;
        }
      }
    });

    return filteredData;
  });

  if (excelData.length === 0) return;

  // Track the export event
  trackExportEvent(
    'excel',
    year,
    projects.length,
    visibleColumns.map(col => col.id),
    {
      filename: `projects_${year}.xlsx`,
      exportedFieldsCount: Object.keys(excelData[0] || {}).length,
    }
  );

  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `Projects ${year}`);
  XLSX.writeFile(workbook, `projects_${year}.xlsx`);
};

const ProjectTable: React.FC<ProjectTableProps> = ({
  projects,
  selectedYear,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const { columns, visibleColumns, updateColumns, resetColumns, isLoading } =
    useColumnConfig();

  // Filter projects by selected year
  const yearProjects = projects.filter(
    project =>
      project?.createdAt &&
      new Date(project.createdAt).getFullYear() === selectedYear
  );

  // Handle sorting
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort projects
  const sortedProjects = React.useMemo(() => {
    if (!sortConfig) return yearProjects;

    return [...yearProjects].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case 'startDate':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'gfa':
          aValue = a.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          bValue = b.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          break;
        case 'impactPerGfa':
          const aGfa =
            a.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          const bGfa =
            b.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          const aTotalImpact =
            a.latestSnapshot?.snapshotData?.climateImpact?.a1_a3 || 0;
          const bTotalImpact =
            b.latestSnapshot?.snapshotData?.climateImpact?.a1_a3 || 0;
          aValue = aGfa > 0 ? aTotalImpact / aGfa : 0;
          bValue = bGfa > 0 ? bTotalImpact / bGfa : 0;
          break;
        case 'totalImpact':
          aValue = a.latestSnapshot?.snapshotData?.climateImpact?.a1_a3 || 0;
          bValue = b.latestSnapshot?.snapshotData?.climateImpact?.a1_a3 || 0;
          break;
        case 'a4PerGfa': {
          const aA4Gfa = a.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          const bA4Gfa = b.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          const aA4 = a.latestSnapshot?.snapshotData?.climateImpact?.a4 || 0;
          const bA4 = b.latestSnapshot?.snapshotData?.climateImpact?.a4 || 0;
          aValue = aA4Gfa > 0 ? aA4 / aA4Gfa : 0;
          bValue = bA4Gfa > 0 ? bA4 / bA4Gfa : 0;
          break;
        }
        case 'a4TotalImpact':
          aValue = a.latestSnapshot?.snapshotData?.climateImpact?.a4 || 0;
          bValue = b.latestSnapshot?.snapshotData?.climateImpact?.a4 || 0;
          break;
        case 'a5PerGfa': {
          const aA5Gfa = a.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          const bA5Gfa = b.latestSnapshot?.snapshotData?.projectSettings?.gfa || 0;
          const aA5 = a.latestSnapshot?.snapshotData?.climateImpact?.a5 || 0;
          const bA5 = b.latestSnapshot?.snapshotData?.climateImpact?.a5 || 0;
          aValue = aA5Gfa > 0 ? aA5 / aA5Gfa : 0;
          bValue = bA5Gfa > 0 ? bA5 / bA5Gfa : 0;
          break;
        }
        case 'a5TotalImpact':
          aValue = a.latestSnapshot?.snapshotData?.climateImpact?.a5 || 0;
          bValue = b.latestSnapshot?.snapshotData?.climateImpact?.a5 || 0;
          break;
        case 'dataAccuracy':
          const aDataAccuracy = a.latestSnapshot?.snapshotData?.dataAccuracy;
          const bDataAccuracy = b.latestSnapshot?.snapshotData?.dataAccuracy;
          aValue = aDataAccuracy
            ? (aDataAccuracy.numberOfSpecificProducts /
                aDataAccuracy.numberOfProducts) *
              100
            : 0;
          bValue = bDataAccuracy
            ? (bDataAccuracy.numberOfSpecificProducts /
                bDataAccuracy.numberOfProducts) *
              100
            : 0;
          break;
        case 'buildingType':
          aValue = a.buildingType?.label || 'Unknown';
          bValue = b.buildingType?.label || 'Unknown';
          break;
        // Circularity sorting cases
        case 'totalMaterialKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.totalMaterialKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.totalMaterialKg || 0;
          break;
        case 'inflowReusedMaterialsKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.reusedMaterialsKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.reusedMaterialsKg || 0;
          break;
        case 'inflowPreservedMaterialsKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.preservedMaterialsKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.preservedMaterialsKg || 0;
          break;
        case 'inflowRecycledPreConsumerKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.recycledContentPreConsumerKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.recycledContentPreConsumerKg || 0;
          break;
        case 'inflowRecycledPostConsumerKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.recycledContentPostConsumerKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.recycledContentPostConsumerKg || 0;
          break;
        case 'inflowBiomassMaterialsKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.biomassMaterialsKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.biomassMaterialsKg || 0;
          break;
        case 'inflowTotalKgCO2eSaved':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.totalKgCO2eSaved || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.inflow
              ?.totalKgCO2eSaved || 0;
          break;
        case 'outflowReusableMaterialsKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.reusableMaterialsKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.reusableMaterialsKg || 0;
          break;
        case 'outflowRecyclableMaterialsKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.recyclableMaterialsKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.recyclableMaterialsKg || 0;
          break;
        case 'outflowDemolitionSystemsKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.demolitionSystemsKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.demolitionSystemsKg || 0;
          break;
        case 'outflowInstallationSystemsKg':
          aValue =
            a.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.installationSystemsKg || 0;
          bValue =
            b.latestSnapshot?.snapshotData?.circularity?.outflow
              ?.installationSystemsKg || 0;
          break;
        default:
          aValue = a[sortConfig.key as keyof ProjectData];
          bValue = b[sortConfig.key as keyof ProjectData];
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [yearProjects, sortConfig]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProjects = sortedProjects.slice(startIndex, endIndex);

  // Reset to first page when year changes or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, sortConfig]);

  // Show loading state while column configuration is loading
  if (isLoading) {
    return (
      <div className="max-w-[1440px] mx-auto">
        <div className="bg-white rounded-xl border border-[#ebedf0] shadow-sm p-8 text-center">
          <div className="text-[16px] text-[#696969]">
            Loading table configuration...
          </div>
        </div>
      </div>
    );
  }

  // Column render functions
  const renderColumnContent = (column: any, project: any) => {
    const snapshot = project.latestSnapshot?.snapshotData;
    const totalImpact = snapshot?.climateImpact?.a1_a3 || 0;
    const gfa = snapshot?.projectSettings?.gfa || project.size;
    const impactPerM2 = gfa > 0 ? totalImpact / gfa : 0;
    const a4Impact = snapshot?.climateImpact?.a4 || 0;
    const a4PerM2 = gfa > 0 ? a4Impact / gfa : 0;
    const a5Impact = snapshot?.climateImpact?.a5 || 0;
    const a5PerM2 = gfa > 0 ? a5Impact / gfa : 0;
    const budgetStatus = getCO2BudgetStatus(project);
    const dataAccuracy = snapshot?.dataAccuracy;
    const accuracyPercentage =
      dataAccuracy && dataAccuracy.numberOfProducts > 0
        ? Math.round(
            (dataAccuracy.numberOfSpecificProducts /
              dataAccuracy.numberOfProducts) *
              100
          )
        : 0;
    const accuracyLevel = getDataAccuracyLevel(accuracyPercentage);

    // Circularity data
    const circularity = snapshot?.circularity;
    const totalMaterialKg = circularity?.totalMaterialKg || 0;
    const inflow = circularity?.inflow;
    const outflow = circularity?.outflow;

    switch (column.id) {
      case 'startDate':
        return (
          <TableCellTooltip
            tooltip={`${new Date(project.createdAt).toLocaleDateString(
              'en-GB',
              {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              }
            )}`}
            truncate={false}
          >
            {formatDateAsMonthYear(project.createdAt)}
          </TableCellTooltip>
        );
      case 'name':
        return (
          <TableCellTooltip tooltip={project.name}>
            {project.name}
          </TableCellTooltip>
        );
      case 'buildingType':
        return (
          <div className="truncate">
            {project.buildingType?.type === 'mixed' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default text-blue-600">
                    {project.buildingType.label}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-gray-900 text-white border-gray-700">
                  <div>
                    <div className="font-medium mb-1">Mixed Use Building</div>
                    {project.buildingType.details?.map(
                      (detail: string, index: number) => (
                        <div key={index} className="text-xs">
                          • {detail}
                        </div>
                      )
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : (
              <TableCellTooltip
                tooltip={project.buildingType?.label || 'Unknown'}
              >
                {project.buildingType?.label || 'Unknown'}
              </TableCellTooltip>
            )}
          </div>
        );
      case 'gfa':
        return (
          <TableCellTooltip
            tooltip={`Gross Floor Area: ${formatNumber(gfa)} m²`}
            truncate={false}
          >
            {`${formatNumber(gfa)} m²`}
          </TableCellTooltip>
        );
      case 'impactPerGfa':
        return (
          <TableCellTooltip
            tooltip={`A1-A3 climate impact per square meter: ${formatImpactPerM2(
              impactPerM2
            )}`}
          >
            {formatImpactPerM2(impactPerM2)}
          </TableCellTooltip>
        );
      case 'totalImpact':
        return (
          <TableCellTooltip
            tooltip={`A1-A3 climate impact: ${formatTotalImpact(totalImpact)}`}
          >
            {formatTotalImpact(totalImpact)}
          </TableCellTooltip>
        );
      case 'a4PerGfa':
        return (
          <TableCellTooltip
            tooltip={`A4 climate impact per square meter: ${formatImpactPerM2(a4PerM2)}`}
          >
            {formatImpactPerM2(a4PerM2)}
          </TableCellTooltip>
        );
      case 'a4TotalImpact':
        return (
          <TableCellTooltip
            tooltip={`A4 climate impact: ${formatTotalImpact(a4Impact)}`}
          >
            {formatTotalImpact(a4Impact)}
          </TableCellTooltip>
        );
      case 'a5PerGfa':
        return (
          <TableCellTooltip
            tooltip={`A5 climate impact per square meter: ${formatImpactPerM2(a5PerM2)}`}
          >
            {formatImpactPerM2(a5PerM2)}
          </TableCellTooltip>
        );
      case 'a5TotalImpact':
        return (
          <TableCellTooltip
            tooltip={`A5 climate impact: ${formatTotalImpact(a5Impact)}`}
          >
            {formatTotalImpact(a5Impact)}
          </TableCellTooltip>
        );
      case 'budget':
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className={`${budgetStatus.badgeClass} font-medium`}>
                {budgetStatus.status}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-900 text-white border-gray-700">
              {budgetStatus.tooltipText}
            </TooltipContent>
          </Tooltip>
        );
      case 'dataAccuracy':
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="truncate">
                <span className={accuracyLevel.color}>
                  {accuracyLevel.level} ({formatNumber(accuracyPercentage)}%)
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-900 text-white border-gray-700">
              {dataAccuracy && dataAccuracy.numberOfProducts > 0 ? (
                <div>
                  <div>
                    Specific products: {dataAccuracy.numberOfSpecificProducts}
                  </div>
                  <div>Total products: {dataAccuracy.numberOfProducts}</div>
                  <div className="mt-1 text-xs opacity-75">
                    {dataAccuracy.numberOfSpecificProducts}/
                    {dataAccuracy.numberOfProducts} ={' '}
                    {formatNumber(accuracyPercentage)}%
                  </div>
                </div>
              ) : (
                <div>No data accuracy information available</div>
              )}
            </TooltipContent>
          </Tooltip>
        );
      // Circularity metrics
      case 'totalMaterialKg':
        return (
          <TableCellTooltip
            tooltip={`Total material weight: ${formatWeight(totalMaterialKg)}`}
            truncate={false}
          >
            {formatWeight(totalMaterialKg)}
          </TableCellTooltip>
        );
      case 'inflowReusedMaterialsKg':
        return (
          <TableCellTooltip
            tooltip={`Reused materials (inflow): ${formatWeight(
              inflow?.reusedMaterialsKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(inflow?.reusedMaterialsKg || 0)}
          </TableCellTooltip>
        );
      case 'inflowPreservedMaterialsKg':
        return (
          <TableCellTooltip
            tooltip={`Preserved materials (inflow): ${formatWeight(
              inflow?.preservedMaterialsKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(inflow?.preservedMaterialsKg || 0)}
          </TableCellTooltip>
        );
      case 'inflowRecycledPreConsumerKg':
        return (
          <TableCellTooltip
            tooltip={`Recycled pre-consumer content (inflow): ${formatWeight(
              inflow?.recycledContentPreConsumerKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(inflow?.recycledContentPreConsumerKg || 0)}
          </TableCellTooltip>
        );
      case 'inflowRecycledPostConsumerKg':
        return (
          <TableCellTooltip
            tooltip={`Recycled post-consumer content (inflow): ${formatWeight(
              inflow?.recycledContentPostConsumerKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(inflow?.recycledContentPostConsumerKg || 0)}
          </TableCellTooltip>
        );
      case 'inflowBiomassMaterialsKg':
        return (
          <TableCellTooltip
            tooltip={`Biomass materials (inflow): ${formatWeight(
              inflow?.biomassMaterialsKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(inflow?.biomassMaterialsKg || 0)}
          </TableCellTooltip>
        );
      case 'inflowTotalKgCO2eSaved':
        return (
          <TableCellTooltip
            tooltip={`Total CO₂e saved from inflow materials: ${formatCO2eSaved(
              inflow?.totalKgCO2eSaved || 0
            )}`}
            truncate={false}
          >
            {formatCO2eSaved(inflow?.totalKgCO2eSaved || 0)}
          </TableCellTooltip>
        );
      case 'outflowReusableMaterialsKg':
        return (
          <TableCellTooltip
            tooltip={`Reusable materials (outflow): ${formatWeight(
              outflow?.reusableMaterialsKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(outflow?.reusableMaterialsKg || 0)}
          </TableCellTooltip>
        );
      case 'outflowRecyclableMaterialsKg':
        return (
          <TableCellTooltip
            tooltip={`Recyclable materials (outflow): ${formatWeight(
              outflow?.recyclableMaterialsKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(outflow?.recyclableMaterialsKg || 0)}
          </TableCellTooltip>
        );
      case 'outflowDemolitionSystemsKg':
        return (
          <TableCellTooltip
            tooltip={`Demolition systems (outflow): ${formatWeight(
              outflow?.demolitionSystemsKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(outflow?.demolitionSystemsKg || 0)}
          </TableCellTooltip>
        );
      case 'outflowInstallationSystemsKg':
        return (
          <TableCellTooltip
            tooltip={`Installation systems (outflow): ${formatWeight(
              outflow?.installationSystemsKg || 0
            )}`}
            truncate={false}
          >
            {formatWeight(outflow?.installationSystemsKg || 0)}
          </TableCellTooltip>
        );
      case 'actions':
        return project.hasViewAccess ? (
          <button
            className="bg-white border border-[#e9e9e8] rounded-lg px-3 py-1 text-[14px] font-semibold text-[#081e0a] shadow-md"
            onClick={() => {
              // Track the project view event
              trackProjectViewEvent(
                project.id,
                project.name,
                project.buildingType?.label,
                {
                  gfa: snapshot?.projectSettings?.gfa,
                  totalImpact: totalImpact,
                  dataAccuracy: accuracyPercentage,
                  url: `https://app.prodikt.com/project/${project.id}`,
                }
              );
              window.open(
                `https://app.prodikt.com/project/${project.id}`,
                '_blank'
              );
            }}
          >
            View
          </button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-[14px] font-semibold text-gray-400 cursor-not-allowed"
                disabled
              >
                View
              </button>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-900 text-white border-gray-700">
              You don't have access to view this project
            </TooltipContent>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  // Sort indicator component
  const SortIndicator = ({ column }: { column: string }) => {
    if (!sortConfig || sortConfig.key !== column) {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 12 12"
          fill="none"
          className="ml-1 opacity-50"
        >
          <path d="M6 2L8 4H4L6 2Z" fill="currentColor" />
          <path d="M6 10L4 8H8L6 10Z" fill="currentColor" />
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg
        width="24"
        height="24"
        viewBox="0 0 12 12"
        fill="none"
        className="ml-1"
      >
        <path d="M6 2L8 4H4L6 2Z" fill="currentColor" />
      </svg>
    ) : (
      <svg
        width="24"
        height="24"
        viewBox="0 0 12 12"
        fill="none"
        className="ml-1"
      >
        <path d="M6 10L4 8H8L6 10Z" fill="currentColor" />
      </svg>
    );
  };

  return (
    <div className="max-w-[1440px] mx-auto">
      <div className="bg-white rounded-xl border border-[#ebedf0] shadow-sm">
        <div className="border-b border-[#e9e9e8] px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[16px] font-semibold text-[#696969]">
                Project overview
              </h2>
            </div>
            <div className="flex items-center gap-4">
              {sortedProjects.length > 0 && (
                <>
                  <div className="flex items-center gap-2">
                    <ColumnConfigModal
                      columns={columns}
                      onUpdateColumns={updateColumns}
                      onResetColumns={resetColumns}
                    />
                    {/* <button
                      onClick={() => exportToCSV(yearProjects, selectedYear, visibleColumns)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#e9e9e8] rounded-lg text-[14px] font-medium text-[#636b63] hover:bg-[#f8f9fa] transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[#636b63]">
                        <path d="M14 10v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2M8 12V4m0 0L5 7m3-3l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Export CSV
                    </button> */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        exportToExcel(
                          yearProjects,
                          selectedYear,
                          visibleColumns
                        )
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className="text-[#000]"
                      >
                        <path
                          d="M14 10v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2M8 12V4m0 0L5 7m3-3l3 3"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Export Excel
                    </Button>
                  </div>
                  <div className="text-[14px] text-[#636b63]">
                    Showing {startIndex + 1}-
                    {Math.min(endIndex, sortedProjects.length)} of{' '}
                    {sortedProjects.length} projects
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-[#e9e9e8]">
                {visibleColumns.map(column => (
                  <th
                    key={column.id}
                    className={`${
                      column.width
                    } px-3 py-3 text-left text-[14px] font-medium text-[#636b63] ${
                      column.sortable
                        ? 'cursor-pointer hover:bg-[#f8f9fa] transition-colors'
                        : ''
                    }`}
                    onClick={
                      column.sortable ? () => handleSort(column.key) : undefined
                    }
                  >
                    {column.sortable ? (
                      <div className="flex items-center">
                        {column.label}
                        <SortIndicator column={column.key} />
                      </div>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedProjects.map((project, index) => (
                <tr
                  key={project.id}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-[#fbfaf9]'}
                >
                  {visibleColumns.map(column => (
                    <td
                      key={column.id}
                      className={`px-3 py-4 text-[14px] font-medium ${
                        column.id === 'actions'
                          ? 'text-center'
                          : 'text-[#081e0a] truncate cursor-default'
                      }`}
                    >
                      {renderColumnContent(column, project)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center px-6 py-4 border-t border-[#e9e9e8]">
            <div className="flex items-center space-x-2">
              {/* Previous button */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center justify-center w-8 h-8 rounded-md border border-[#e9e9e8] bg-white text-[#636b63] hover:bg-[#f8f9fa] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber: number;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`flex items-center justify-center w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                      currentPage === pageNumber
                        ? 'bg-[#2a654e] text-white'
                        : 'border border-[#e9e9e8] bg-white text-[#636b63] hover:bg-[#f8f9fa]'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              {/* Next button */}
              <button
                onClick={() =>
                  setCurrentPage(prev => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="flex items-center justify-center w-8 h-8 rounded-md border border-[#e9e9e8] bg-white text-[#636b63] hover:bg-[#f8f9fa] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 4L10 8L6 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTable;
